import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../../services/api';
import MissionRequestFormSection from './MissionRequestFormSection';
import { parseMissionFormData } from '../../utils/parseMissionFormData';
import { 
  FiArrowLeft, FiTruck, FiCalendar, FiMapPin, 
  FiEdit3, FiSave, FiX, FiUser, FiMail, FiPhone,
  FiDollarSign, FiPackage, FiClock, FiCheckCircle,
  FiXCircle, FiAlertCircle, FiHome, FiNavigation,
  FiLayers, FiSettings
} from 'react-icons/fi';

// Fonction pour géocoder une adresse
const geocodeAddress = async (address) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
    );
    const data = await response.json();
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon)
      };
    }
    return null;
  } catch (error) {
    console.error('Erreur de géocodage:', error);
    return null;
  }
};

// Fonction pour obtenir une route entre deux points
const getRoute = async (fromLat, fromLng, toLat, toLng) => {
  try {
    const response = await fetch(
      `https://router.project-osrm.org/route/v1/driving/${fromLng},${fromLat};${toLng},${toLat}?overview=full&geometries=geojson`
    );
    const data = await response.json();
    if (data && data.code === 'Ok' && data.routes && data.routes.length > 0) {
      return data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]); // Convertir [lng, lat] en [lat, lng]
    }
    return null;
  } catch (error) {
    console.error('Erreur lors du calcul de la route:', error);
    return null;
  }
};

// Composant pour gérer le rendu dynamique de la carte
const MapComponent = ({ quote }) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!isClient || !quote?.from_lat || !quote?.from_lng || !quote?.to_lat || !quote?.to_lng) {
    return null;
  }

  return (
    <MapContainer
      center={[
        (parseFloat(quote.from_lat) + parseFloat(quote.to_lat)) / 2,
        (parseFloat(quote.from_lng) + parseFloat(quote.to_lng)) / 2
      ]}
      zoom={11}
      style={{ height: '100%', width: '100%', zIndex: 0 }}
      scrollWheelZoom={true}
      key={`map-${quote.from_lat}-${quote.from_lng}-${quote.to_lat}-${quote.to_lng}`}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <Marker
        position={[parseFloat(quote.from_lat), parseFloat(quote.from_lng)]}
        icon={startIcon}
      >
        <Popup>
          <div className="text-sm">
            <div className="font-semibold text-green-600 mb-1">📍 Départ</div>
            <div className="text-gray-700">{quote.from_address}</div>
          </div>
        </Popup>
      </Marker>
      <Marker
        position={[parseFloat(quote.to_lat), parseFloat(quote.to_lng)]}
        icon={endIcon}
      >
        <Popup>
          <div className="text-sm">
            <div className="font-semibold text-red-600 mb-1">📍 Arrivée</div>
            <div className="text-gray-700">{quote.to_address}</div>
          </div>
        </Popup>
      </Marker>
      <RoutePolyline 
        fromLat={parseFloat(quote.from_lat)} 
        fromLng={parseFloat(quote.from_lng)}
        toLat={parseFloat(quote.to_lat)} 
        toLng={parseFloat(quote.to_lng)}
      />
    </MapContainer>
  );
};

// Composant pour afficher une route réelle entre deux points
const RoutePolyline = ({ fromLat, fromLng, toLat, toLng }) => {
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRoute = async () => {
      try {
        setLoading(true);
        const routeCoords = await getRoute(fromLat, fromLng, toLat, toLng);
        if (routeCoords) {
          setRoute(routeCoords);
        } else {
          // Si le routage échoue, utiliser une ligne droite
          setRoute([
            [fromLat, fromLng],
            [toLat, toLng]
          ]);
        }
      } catch (error) {
        console.error('Erreur lors du calcul de la route:', error);
        // En cas d'erreur, utiliser une ligne droite
        setRoute([
          [fromLat, fromLng],
          [toLat, toLng]
        ]);
      } finally {
        setLoading(false);
      }
    };

    if (fromLat && fromLng && toLat && toLng) {
      fetchRoute();
    }
  }, [fromLat, fromLng, toLat, toLng]);

  if (loading || !route) {
    // Afficher une ligne droite temporaire pendant le chargement
    return (
      <Polyline
        positions={[
          [fromLat, fromLng],
          [toLat, toLng]
        ]}
        pathOptions={{ color: '#3b82f6', weight: 5, opacity: 0.5, dashArray: '10, 5' }}
      />
    );
  }

  return (
    <Polyline
      positions={route}
      pathOptions={{ color: '#3b82f6', weight: 5, opacity: 0.8 }}
    />
  );
};

// Composant pour géocoder les adresses et afficher la carte
const GeocodedMapComponent = ({ quote }) => {
  const [isClient, setIsClient] = useState(false);
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    const fetchCoords = async () => {
      if (!quote?.from_address || !quote?.to_address) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [fromCoords, toCoords] = await Promise.all([
          geocodeAddress(quote.from_address),
          geocodeAddress(quote.to_address)
        ]);

        if (fromCoords && toCoords) {
          setCoords({ from: fromCoords, to: toCoords });
        } else {
          setError('Impossible de géocoder les adresses');
        }
      } catch (err) {
        console.error('Erreur lors du géocodage:', err);
        setError('Erreur lors du géocodage des adresses');
      } finally {
        setLoading(false);
      }
    };

    if (isClient && quote?.from_address && quote?.to_address) {
      fetchCoords();
    }
  }, [isClient, quote?.from_address, quote?.to_address]);

  if (!isClient || loading) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm">Géocodage des adresses en cours...</p>
        </div>
      </div>
    );
  }

  if (error || !coords) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>
          <FiMapPin className="mx-auto text-gray-500 text-4xl mb-3" />
          <p className="text-gray-400 mb-2">{error || 'Coordonnées non disponibles'}</p>
          <p className="text-xs text-gray-500">
            Les coordonnées GPS n'ont pas pu être obtenues à partir des adresses.
          </p>
          {(quote?.from_address || quote?.to_address) && (
            <div className="mt-4 text-xs text-gray-500">
              <p>Adresses disponibles :</p>
              {quote?.from_address && <p className="mt-1">📍 Départ: {quote.from_address}</p>}
              {quote?.to_address && <p className="mt-1">📍 Arrivée: {quote.to_address}</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden" style={{ height: '400px', zIndex: 0, position: 'relative' }}>
        <MapContainer
          center={[
            (coords.from.lat + coords.to.lat) / 2,
            (coords.from.lng + coords.to.lng) / 2
          ]}
          zoom={11}
          style={{ height: '100%', width: '100%', zIndex: 0 }}
          scrollWheelZoom={true}
          key={`map-geocoded-${coords.from.lat}-${coords.from.lng}-${coords.to.lat}-${coords.to.lng}`}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker
            position={[coords.from.lat, coords.from.lng]}
            icon={startIcon}
          >
            <Popup>
              <div className="text-sm">
                <div className="font-semibold text-green-600 mb-1">📍 Départ</div>
                <div className="text-gray-700">{quote.from_address}</div>
              </div>
            </Popup>
          </Marker>
          <Marker
            position={[coords.to.lat, coords.to.lng]}
            icon={endIcon}
          >
            <Popup>
              <div className="text-sm">
                <div className="font-semibold text-red-600 mb-1">📍 Arrivée</div>
                <div className="text-gray-700">{quote.to_address}</div>
              </div>
            </Popup>
          </Marker>
          <RoutePolyline 
            fromLat={coords.from.lat} 
            fromLng={coords.from.lng}
            toLat={coords.to.lat} 
            toLng={coords.to.lng}
          />
        </MapContainer>
      </div>
      <div className="flex items-center justify-center space-x-6 mt-3 text-xs text-gray-400">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
          <span>Départ</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
          <span>Arrivée</span>
        </div>
                    <div className="flex items-center">
                      <div className="w-8 h-0.5 bg-blue-500 mr-2"></div>
                      <span>Itinéraire</span>
                    </div>
      </div>
    </>
  );
};

// Configuration des icônes Leaflet
if (L.Icon.Default.prototype._getIconUrl) {
  delete L.Icon.Default.prototype._getIconUrl;
}
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Icône personnalisée pour le départ
const startIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// Icône personnalisée pour l'arrivée
const endIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// Formater les dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Formater le montant
const formatPrice = (cents) => {
  if (!cents) return '0,00 DT';
  return `${(cents / 100).toFixed(2).replace('.', ',')} DT`;
};

// Obtenir la couleur du statut
const getStatusColor = (status) => {
  switch(status) {
    case 'confirmé':
    case 'confirmed':
    case 'accepted':
      return 'bg-green-600/20 text-green-400 border border-green-600/50';
    case 'en_attente':
    case 'pending':
      return 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/50';
    case 'annulé':
    case 'cancelled':
    case 'rejected':
      return 'bg-red-600/20 text-red-400 border border-red-600/50';
    case 'terminé':
    case 'completed':
      return 'bg-blue-600/20 text-blue-400 border border-blue-600/50';
    default:
      return 'bg-gray-700 text-gray-300 border border-gray-600';
  }
};

const getStatusLabel = (status) => {
  const labels = {
    'confirmé': 'Confirmé',
    'confirmed': 'Confirmé',
    'accepted': 'Accepté',
    'en_attente': 'En attente',
    'pending': 'En attente',
    'annulé': 'Annulé',
    'cancelled': 'Annulé',
    'rejected': 'Annulé',
    'terminé': 'Terminé',
    'completed': 'Terminé'
  };
  return labels[status] || status;
};

function MoveDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [move, setMove] = useState(null);
  const [missionForm, setMissionForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    status: '',
    price_cents: '',
    move_date: '',
    description: ''
  });

  useEffect(() => {
    if (id) {
      loadMoveDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadMoveDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.getMoveDetails(id);
      
      if (response && response.success) {
        const quoteData = response.data.quote;
        setQuote(quoteData);
        setMove(response.data.move);
        setMissionForm(
          response.data.missionForm || parseMissionFormData(quoteData)
        );
        setFormData({
          status: response.data.quote.status || '',
          price_cents: response.data.quote.price_cents || '',
          move_date: response.data.quote.move_date || '',
          description: response.data.quote.description || ''
        });
      } else {
        throw new Error(response.message || 'Erreur lors du chargement des détails');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des détails:', err);
      setError(err.message || 'Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // TODO: Implémenter la mise à jour du quote
      // const response = await api.updateQuote(id, formData);
      
      setIsEditing(false);
      loadMoveDetails();
    } catch (err) {
      setError(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-700"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-transparent border-t-yellow-500 absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  if (error && !quote) {
    return (
      <div className="space-y-6">
        <div className="bg-red-900/30 border-l-4 border-red-500 rounded-lg p-6">
          <div className="flex items-center">
            <FiAlertCircle className="h-6 w-6 text-red-500 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-red-300">Erreur</h3>
              <p className="text-red-300">{error}</p>
            </div>
          </div>
        </div>
        <Link
          to="/admin/moves"
          className="inline-flex items-center px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all"
        >
          <FiArrowLeft className="mr-2" />
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/admin/moves"
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiArrowLeft className="w-6 h-6 text-gray-300" />
          </Link>
          <div>
            <h1 className="text-4xl font-bold text-yellow-400">
              Détails du déménagement
            </h1>
            <p className="text-gray-400 font-medium mt-1">
              Informations complètes du devis
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all font-semibold flex items-center shadow-lg shadow-yellow-500/30 disabled:opacity-50"
              >
                <FiSave className="mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    status: quote.status || '',
                    price_cents: quote.price_cents || '',
                    move_date: quote.move_date || '',
                    description: quote.description || ''
                  });
                }}
                className="px-4 py-2 border border-gray-700 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-all font-semibold"
              >
                <FiX className="mr-2 inline" />
                Annuler
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all font-semibold flex items-center shadow-lg shadow-yellow-500/30"
            >
              <FiEdit3 className="mr-2" />
              Modifier
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/30 border-l-4 border-red-500 rounded-lg p-4">
          <div className="flex items-center">
            <FiAlertCircle className="h-5 w-5 text-red-500 mr-3" />
            <p className="text-sm font-semibold text-red-300">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-300">
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Quote Info Card */}
      <div className="bg-gray-800/80 backdrop-blur-xl border border-yellow-600/30 rounded-xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b border-yellow-600/30 px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="w-20 h-20 bg-yellow-600/20 border-2 border-yellow-600/50 rounded-2xl flex items-center justify-center text-yellow-400 text-3xl shadow-lg shadow-yellow-500/20">
                <FiTruck />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-yellow-400 mb-2">
                  Devis #{quote?.id?.substring(0, 8).toUpperCase()}
                </h2>
                <div className="flex items-center space-x-4 mt-3">
                  <span className={`px-4 py-1.5 inline-flex items-center text-sm font-bold rounded-full shadow-sm ${getStatusColor(quote?.status)}`}>
                    {getStatusLabel(quote?.status)}
                  </span>
                  {quote?.price_cents && (
                    <div className="flex items-center space-x-2 px-4 py-1.5 bg-yellow-600/20 border border-yellow-600/50 rounded-full">
                      <FiDollarSign className="text-yellow-400" />
                      <span className="text-yellow-400 font-bold text-lg">
                        {formatPrice(quote.price_cents)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-8 bg-gray-800">
          {/* Informations générales */}
          <div className="mb-8">
            <div className="flex items-center mb-6 pb-4 border-b border-gray-700">
              <div className="w-10 h-10 bg-yellow-600/20 border border-yellow-600/50 rounded-lg flex items-center justify-center mr-3">
                <FiSettings className="text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-100">
                Informations générales
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Date de demande</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                  <FiCalendar className="mr-2 text-gray-400" />
                  {formatDate(quote?.requested_at || quote?.created_at)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Date du déménagement</label>
                {isEditing ? (
                  <input
                    type="datetime-local"
                    value={formData.move_date ? new Date(formData.move_date).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setFormData({...formData, move_date: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  />
                ) : (
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                    <FiCalendar className="mr-2 text-gray-400" />
                    {formatDate(quote?.move_date)}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Statut</label>
                {isEditing ? (
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  >
                    <option value="pending">En attente</option>
                    <option value="confirmed">Confirmé</option>
                    <option value="accepted">Accepté</option>
                    <option value="completed">Terminé</option>
                    <option value="cancelled">Annulé</option>
                    <option value="rejected">Annulé</option>
                  </select>
                ) : (
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg">
                    <span className={`px-3 py-1 inline-flex text-xs font-bold rounded-full ${getStatusColor(quote?.status)}`}>
                      {getStatusLabel(quote?.status)}
                    </span>
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Prix</label>
                {isEditing ? (
                  <input
                    type="number"
                    value={formData.price_cents ? formData.price_cents / 100 : ''}
                    onChange={(e) => setFormData({...formData, price_cents: Math.round(e.target.value * 100)})}
                    placeholder="0.00"
                    step="0.01"
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  />
                ) : (
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                    <FiDollarSign className="mr-2 text-gray-400" />
                    {formatPrice(quote?.price_cents)}
                  </p>
                )}
              </div>
            </div>
          </div>

          <MissionRequestFormSection missionForm={missionForm} />

          {/* Adresses */}
          <div className="mb-8 pt-8 border-t border-gray-700">
            <div className="flex items-center mb-6 pb-4 border-b border-gray-700">
              <div className="w-10 h-10 bg-yellow-600/20 border border-yellow-600/50 rounded-lg flex items-center justify-center mr-3">
                <FiMapPin className="text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-100">
                Adresses
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Adresse de départ</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                  <FiMapPin className="mr-2 text-green-400 flex-shrink-0" />
                  {quote?.from_address || 'N/A'}
                </p>
                {quote?.from_lat && quote?.from_lng && (
                  <p className="text-xs text-gray-400 mt-1 ml-6">
                    Coordonnées: {quote.from_lat}, {quote.from_lng}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Adresse d'arrivée</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                  <FiMapPin className="mr-2 text-red-400 flex-shrink-0" />
                  {quote?.to_address || 'N/A'}
                </p>
                {quote?.to_lat && quote?.to_lng && (
                  <p className="text-xs text-gray-400 mt-1 ml-6">
                    Coordonnées: {quote.to_lat}, {quote.to_lng}
                  </p>
                )}
              </div>
            </div>
            
            {/* Carte de l'itinéraire */}
            <div className="mt-6">
              <label className="block text-sm font-semibold text-gray-300 mb-3">Carte de l'itinéraire</label>
              {(() => {
                // Si les coordonnées sont disponibles, afficher la carte
                if (quote?.from_lat && quote?.from_lng && quote?.to_lat && quote?.to_lng) {
                  return (
                    <>
                      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden" style={{ height: '400px', zIndex: 0, position: 'relative' }}>
                        <MapComponent quote={quote} />
                      </div>
                      <div className="flex items-center justify-center space-x-6 mt-3 text-xs text-gray-400">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                          <span>Départ</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                          <span>Arrivée</span>
                        </div>
                    <div className="flex items-center">
                      <div className="w-8 h-0.5 bg-blue-500 mr-2"></div>
                      <span>Itinéraire</span>
                    </div>
                      </div>
                    </>
                  );
                }
                
                // Si les adresses sont disponibles mais pas les coordonnées, essayer de géocoder
                if (quote?.from_address && quote?.to_address) {
                  return <GeocodedMapComponent quote={quote} />;
                }
                
                // Sinon, afficher le message d'erreur
                return (
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div>
                      <FiMapPin className="mx-auto text-gray-500 text-4xl mb-3" />
                      <p className="text-gray-400 mb-2">Coordonnées non disponibles</p>
                      <p className="text-xs text-gray-500">
                        Les coordonnées GPS des adresses de départ et d'arrivée sont nécessaires pour afficher la carte.
                      </p>
                      {(quote?.from_address || quote?.to_address) && (
                        <div className="mt-4 text-xs text-gray-500">
                          <p>Adresses disponibles :</p>
                          {quote?.from_address && <p className="mt-1">📍 Départ: {quote.from_address}</p>}
                          {quote?.to_address && <p className="mt-1">📍 Arrivée: {quote.to_address}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Détails du déménagement */}
          <div className="mb-8 pt-8 border-t border-gray-700">
            <div className="flex items-center mb-6 pb-4 border-b border-gray-700">
              <div className="w-10 h-10 bg-yellow-600/20 border border-yellow-600/50 rounded-lg flex items-center justify-center mr-3">
                <FiPackage className="text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-100">
                Détails du déménagement
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Volume (m³)</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                  {quote?.volume_m3 ? `${quote.volume_m3} m³` : 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Type de camion</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                  {quote?.truck_type || 'N/A'}
                </p>
              </div>
              {quote?.housing_type && (
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Type de logement</label>
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                    {quote.housing_type}
                  </p>
                </div>
              )}
              {quote?.floor !== undefined && quote?.floor !== null && (
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Étage</label>
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                    {quote.floor}
                  </p>
                </div>
              )}
              {quote?.has_elevator !== undefined && quote?.has_elevator !== null && (
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Ascenseur</label>
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                    {quote.has_elevator ? (
                      <span className="flex items-center text-green-400">
                        <FiCheckCircle className="mr-2" />
                        Oui
                      </span>
                    ) : (
                      <span className="flex items-center text-gray-400">
                        <FiXCircle className="mr-2" />
                        Non
                      </span>
                    )}
                  </p>
                </div>
              )}
              {quote?.services && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Services</label>
                  <div className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg">
                    {(() => {
                      try {
                        const services = typeof quote.services === 'string' 
                          ? JSON.parse(quote.services) 
                          : quote.services;
                        
                        return (
                          <div className="space-y-2">
                            {services.article && (
                              <div className="flex items-center text-white">
                                <FiPackage className="mr-2 text-yellow-400" />
                                <span className="text-sm">
                                  <span className="font-semibold">Article:</span> {services.article}
                                </span>
                              </div>
                            )}
                            {services.serviceType && (
                              <div className="flex items-center text-white">
                                <FiSettings className="mr-2 text-yellow-400" />
                                <span className="text-sm">
                                  <span className="font-semibold">Type de service:</span>{' '}
                                  <span className="capitalize">
                                    {services.serviceType === 'transport' ? 'Transport' :
                                     services.serviceType === 'packing' ? 'Emballage' :
                                     services.serviceType === 'unpacking' ? 'Déballage' :
                                     services.serviceType === 'assembly' ? 'Montage' :
                                     services.serviceType === 'disassembly' ? 'Démontage' :
                                     services.serviceType}
                                  </span>
                                </span>
                              </div>
                            )}
                            {services.isQuickService !== undefined && (
                              <div className="flex items-center text-white">
                                {services.isQuickService ? (
                                  <>
                                    <FiCheckCircle className="mr-2 text-green-400" />
                                    <span className="text-sm">
                                      <span className="font-semibold">Service rapide:</span> Oui
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <FiXCircle className="mr-2 text-gray-400" />
                                    <span className="text-sm">
                                      <span className="font-semibold">Service rapide:</span> Non
                                    </span>
                                  </>
                                )}
                              </div>
                            )}
                            {Object.keys(services).filter(key => !['article', 'serviceType', 'isQuickService'].includes(key)).length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-700">
                                <p className="text-sm font-semibold text-gray-300 mb-4">Autres informations:</p>
                                <div className="space-y-4">
                                  {(() => {
                                    const otherInfo = Object.fromEntries(
                                      Object.entries(services).filter(([key]) => 
                                        !['article', 'serviceType', 'isQuickService'].includes(key)
                                      )
                                    );
                                    
                                    return (
                                      <>
                                        {/* Type de propriété */}
                                        {otherInfo.propertyType && (
                                          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                                            <div className="flex items-center mb-2">
                                              <FiHome className="mr-2 text-yellow-400" />
                                              <span className="text-sm font-semibold text-white">Type de propriété</span>
                                            </div>
                                            <p className="text-sm text-gray-300 ml-7 capitalize">
                                              {otherInfo.propertyType === 'maison' ? 'Maison' :
                                               otherInfo.propertyType === 'appartement' ? 'Appartement' :
                                               otherInfo.propertyType}
                                            </p>
                                          </div>
                                        )}
                                        
                                        {/* Étage */}
                                        {otherInfo.floor !== undefined && otherInfo.floor !== null && otherInfo.floor !== '' && (
                                          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                                            <div className="flex items-center mb-2">
                                              <FiLayers className="mr-2 text-yellow-400" />
                                              <span className="text-sm font-semibold text-white">Étage</span>
                                            </div>
                                            <p className="text-sm text-gray-300 ml-7">
                                              {otherInfo.floor}
                                            </p>
                                          </div>
                                        )}
                                        
                                        {/* Ascenseur */}
                                        {otherInfo.hasElevator !== undefined && (
                                          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                                            <div className="flex items-center mb-2">
                                              {otherInfo.hasElevator ? (
                                                <FiCheckCircle className="mr-2 text-green-400" />
                                              ) : (
                                                <FiXCircle className="mr-2 text-gray-500" />
                                              )}
                                              <span className="text-sm font-semibold text-white">Ascenseur</span>
                                            </div>
                                            <p className={`text-sm ml-7 ${otherInfo.hasElevator ? 'text-green-400' : 'text-gray-400'}`}>
                                              {otherInfo.hasElevator ? 'Oui' : 'Non'}
                                            </p>
                                          </div>
                                        )}
                                        
                                        {/* Notes */}
                                        {otherInfo.notes && (
                                          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                                            <div className="flex items-center mb-2">
                                              <FiEdit3 className="mr-2 text-yellow-400" />
                                              <span className="text-sm font-semibold text-white">Notes</span>
                                            </div>
                                            <p className="text-sm text-gray-300 ml-7 whitespace-pre-wrap">
                                              {otherInfo.notes}
                                            </p>
                                          </div>
                                        )}
                                        
                                        {/* Cuisine */}
                                        {otherInfo.cuisine && typeof otherInfo.cuisine === 'object' && (
                                          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                                            <div className="flex items-center mb-3">
                                              <FiSettings className="mr-2 text-yellow-400" />
                                              <span className="text-sm font-semibold text-white">Cuisine</span>
                                            </div>
                                            <div className="ml-7 grid grid-cols-3 gap-3">
                                              {otherInfo.cuisine.petit && otherInfo.cuisine.petit !== '' && (
                                                <div className="bg-gray-800/50 rounded-lg p-2">
                                                  <p className="text-xs text-gray-400 mb-1">Petit</p>
                                                  <p className="text-sm text-white font-medium">{otherInfo.cuisine.petit}</p>
                                                </div>
                                              )}
                                              {otherInfo.cuisine.moyen && otherInfo.cuisine.moyen !== '' && (
                                                <div className="bg-gray-800/50 rounded-lg p-2">
                                                  <p className="text-xs text-gray-400 mb-1">Moyen</p>
                                                  <p className="text-sm text-white font-medium">{otherInfo.cuisine.moyen}</p>
                                                </div>
                                              )}
                                              {otherInfo.cuisine.grande && otherInfo.cuisine.grande !== '' && (
                                                <div className="bg-gray-800/50 rounded-lg p-2">
                                                  <p className="text-xs text-gray-400 mb-1">Grande</p>
                                                  <p className="text-sm text-white font-medium">{otherInfo.cuisine.grande}</p>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Chambres */}
                                        {otherInfo.chambres && Array.isArray(otherInfo.chambres) && otherInfo.chambres.length > 0 && (
                                          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                                            <div className="flex items-center mb-3">
                                              <FiHome className="mr-2 text-yellow-400" />
                                              <span className="text-sm font-semibold text-white">
                                                Chambres ({otherInfo.chambres.length})
                                              </span>
                                            </div>
                                            <div className="ml-7 space-y-3">
                                              {otherInfo.chambres.map((chambre, index) => (
                                                <div key={index} className="bg-gray-800/50 border border-gray-700 rounded-lg p-3">
                                                  {chambre.nom && chambre.nom !== '' && (
                                                    <div className="mb-2">
                                                      <p className="text-xs text-gray-400 mb-1">Nom de la chambre</p>
                                                      <p className="text-sm text-white font-medium">{chambre.nom}</p>
                                                    </div>
                                                  )}
                                                  {chambre.articles && typeof chambre.articles === 'object' && (
                                                    <div>
                                                      <p className="text-xs text-gray-400 mb-2">Articles</p>
                                                      <div className="grid grid-cols-3 gap-2">
                                                        {chambre.articles.petit && chambre.articles.petit !== '0' && chambre.articles.petit !== '' && (
                                                          <div className="bg-gray-700/50 rounded p-2">
                                                            <p className="text-xs text-gray-400">Petit</p>
                                                            <p className="text-sm text-white font-medium">{chambre.articles.petit}</p>
                                                          </div>
                                                        )}
                                                        {chambre.articles.moyen && chambre.articles.moyen !== '0' && chambre.articles.moyen !== '' && (
                                                          <div className="bg-gray-700/50 rounded p-2">
                                                            <p className="text-xs text-gray-400">Moyen</p>
                                                            <p className="text-sm text-white font-medium">{chambre.articles.moyen}</p>
                                                          </div>
                                                        )}
                                                        {chambre.articles.grande && chambre.articles.grande !== '0' && chambre.articles.grande !== '' && (
                                                          <div className="bg-gray-700/50 rounded p-2">
                                                            <p className="text-xs text-gray-400">Grande</p>
                                                            <p className="text-sm text-white font-medium">{chambre.articles.grande}</p>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Quick Service Group ID */}
                                        {otherInfo.quickServiceGroupId && (
                                          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                                            <div className="flex items-center mb-2">
                                              <FiSettings className="mr-2 text-yellow-400" />
                                              <span className="text-sm font-semibold text-white">ID Groupe Service Rapide</span>
                                            </div>
                                            <p className="text-sm text-gray-300 ml-7 font-mono">
                                              {otherInfo.quickServiceGroupId}
                                            </p>
                                          </div>
                                        )}
                                        
                                        {/* Autres champs non formatés */}
                                        {(() => {
                                          const unformattedKeys = Object.keys(otherInfo).filter(key => 
                                            !['propertyType', 'floor', 'hasElevator', 'notes', 'cuisine', 'chambres', 'quickServiceGroupId'].includes(key)
                                          );
                                          
                                          if (unformattedKeys.length > 0) {
                                            return (
                                              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                                                <p className="text-xs text-gray-400 mb-2">Autres données:</p>
                                                <pre className="text-xs text-gray-400 whitespace-pre-wrap bg-gray-800/50 p-2 rounded">
                                                  {JSON.stringify(
                                                    Object.fromEntries(
                                                      unformattedKeys.map(key => [key, otherInfo[key]])
                                                    ),
                                                    null,
                                                    2
                                                  )}
                                                </pre>
                                              </div>
                                            );
                                          }
                                          return null;
                                        })()}
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      } catch (e) {
                        return (
                          <pre className="text-xs text-gray-400 whitespace-pre-wrap">
                            {typeof quote.services === 'string' ? quote.services : JSON.stringify(quote.services, null, 2)}
                          </pre>
                        );
                      }
                    })()}
                  </div>
                </div>
              )}
              {quote?.description && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Description</label>
                  {isEditing ? (
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows={4}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 resize-none"
                    />
                  ) : (
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white whitespace-pre-wrap">
                      {quote.description}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Informations client */}
          <div className="mb-8 pt-8 border-t border-gray-700">
            <div className="flex items-center mb-6 pb-4 border-b border-gray-700">
              <div className="w-10 h-10 bg-yellow-600/20 border border-yellow-600/50 rounded-lg flex items-center justify-center mr-3">
                <FiUser className="text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-100">
                Informations client
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Nom</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                  {quote?.client_name || 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Email</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                  <FiMail className="mr-2 text-gray-400" />
                  {quote?.client_email || 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Téléphone</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                  <FiPhone className="mr-2 text-gray-400" />
                  {quote?.client_phone || 'N/A'}
                </p>
              </div>
              {quote?.client_id && (
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Actions</label>
                  <Link
                    to={`/admin/users/${quote.client_id}`}
                    className="inline-flex items-center px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all font-semibold shadow-lg shadow-yellow-500/30"
                  >
                    <FiUser className="mr-2" />
                    Voir le profil client
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Informations déménageur */}
          {quote?.demenageur_name && (
            <div className="pt-8 border-t border-gray-700">
              <div className="flex items-center mb-6 pb-4 border-b border-gray-700">
                <div className="w-10 h-10 bg-yellow-600/20 border border-yellow-600/50 rounded-lg flex items-center justify-center mr-3">
                  <FiTruck className="text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-100">
                  Informations déménageur
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Nom</label>
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                    {quote.demenageur_name}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Email</label>
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                    <FiMail className="mr-2 text-gray-400" />
                    {quote.demenageur_email || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Téléphone</label>
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                    <FiPhone className="mr-2 text-gray-400" />
                    {quote.demenageur_phone || 'N/A'}
                  </p>
                </div>
                {quote?.mover_id && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Actions</label>
                    <Link
                      to={`/admin/users/${quote.mover_id}`}
                      className="inline-flex items-center px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all font-semibold shadow-lg shadow-yellow-500/30"
                    >
                      <FiUser className="mr-2" />
                      Voir le profil déménageur
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Informations du move (si existe) */}
          {move && (
            <div className="mt-8 pt-8 border-t border-gray-700">
              <div className="flex items-center mb-6 pb-4 border-b border-gray-700">
                <div className="w-10 h-10 bg-yellow-600/20 border border-yellow-600/50 rounded-lg flex items-center justify-center mr-3">
                  <FiClock className="text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-100">
                  Historique d'exécution
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {move.scheduled_date && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Date programmée</label>
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                      <FiCalendar className="mr-2 text-gray-400" />
                      {formatDate(move.scheduled_date)}
                    </p>
                  </div>
                )}
                {move.started_at && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Démarré le</label>
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                      <FiClock className="mr-2 text-gray-400" />
                      {formatDate(move.started_at)}
                    </p>
                  </div>
                )}
                {move.completed_at && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Terminé le</label>
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                      <FiCheckCircle className="mr-2 text-gray-400" />
                      {formatDate(move.completed_at)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MoveDetails;


